import React, { useMemo, useState, useEffect, useRef } from "react";

import { GetCountries, GetState } from "react-country-state-city";
import Select from "react-select";

import {
  ArrowClockwise,
  Calendar3,
  CaretDownFill,
  CaretUpFill,
  Check2Square,
  Coin,
  HandThumbsUpFill,
  TicketPerforatedFill,
  InfoCircleFill,
} from "react-bootstrap-icons";

import "./CheckoutForm.css";
import { Link, useParams } from "react-router-dom";

import ReactPixel from "react-facebook-pixel";
import ReactGA from "react-ga4";

import PaymentSection from "./gateways/PaymentSection";
import { injectScripts } from "../../../../utils/injectScripts";

/* -------------------- Normalised data -------------------- */
const PIRANHA_INTERVAL_RAW = {
  day: "day",
  week: "week",
  month: "month",
  year: "year",
};

const PIRANHA_INTERVAL_NORMALIZED = {
  day: "daily",
  week: "weekly",
  month: "monthly",
  year: "yearly",
};

/* -------------------- General helpers -------------------- */
const getCouponCodeFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return String(params.get("coupon_code") || "").trim();
};

const normalizeCurrency = (c) =>
  String(c || "USD")
    .trim()
    .toUpperCase();

const displayCurrency = (currency) => {
  const cur = normalizeCurrency(currency);
  return cur === "MYR" ? "RM" : cur;
};

const formatMoney = (value, currency = "USD") => {
  const v = Number(value);
  if (!Number.isFinite(v)) return "-";

  const cur = normalizeCurrency(currency);
  const amount = v / 100;

  if (cur === "MYR") {
    return `RM ${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: cur,
    minimumFractionDigits: 2,
  }).format(amount);
};

const getUserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (err) {
    return "UTC";
  }
};

const formatDateTimeInUserTimeZone = (isoString) => {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: getUserTimeZone(),
    }).format(date);
  } catch (err) {
    return "";
  }
};

const buildRecurringPhrase = (interval, intervalCount = 1) => {
  const unit = String(interval || "").toLowerCase();
  const count = Number(intervalCount);

  if (!Number.isFinite(count) || count <= 0 || !unit) return "";

  if (count === 1) {
    if (unit === "day") return "every day";
    if (unit === "week") return "every week";
    if (unit === "month") return "every month";
    if (unit === "year") return "every year";
    return "";
  }

  const pluralUnit =
    unit === "day"
      ? "days"
      : unit === "week"
        ? "weeks"
        : unit === "month"
          ? "months"
          : unit === "year"
            ? "years"
            : "";

  if (!pluralUnit) return "";

  return `every ${count} ${pluralUnit}`;
};

const buildTrialChargeMessage = (coupon) => {
  if (!coupon || coupon?.permission !== 8) return "";

  const formattedDate = formatDateTimeInUserTimeZone(coupon?.trial_ends_at_iso);
  if (!formattedDate) return "";

  if (!coupon?.free_trial_is_cc_required) {
    return `Once your free trial ends on ${formattedDate}, a paid subscription will be required to continue enjoying the member-exclusive benefits.`;
  }

  const formattedAmount = formatMoney(
    coupon?.next_charge_amount,
    coupon?.next_charge_currency || "USD",
  );

  const recurringPhrase = buildRecurringPhrase(
    coupon?.next_charge_interval,
    coupon?.next_charge_interval_count,
  );

  if (formattedAmount && recurringPhrase) {
    return `Once your free trial ends on ${formattedDate}, your subscription will automatically renew and ${formattedAmount} will be charged ${recurringPhrase} unless you cancel before then.`;
  }

  if (formattedAmount) {
    return `Once your free trial ends on ${formattedDate}, ${formattedAmount} will be charged unless you cancel before then.`;
  }

  return `Once your free trial ends on ${formattedDate}, charges will apply unless you cancel before then.`;
};

/* -------------------- Common Email Providers -------------------- */
const COMMON_EMAIL_PROVIDERS = [
  "gmail",
  "google",
  "hotmail",
  "microsoft",
  "outlook",
  "live",
  "yahoo",
  "icloud",
  "qq",
  "163",
  "naver",
  "daum",
  "aol",
  "proton",
  "gmx",
];

/* -------------------- Helpers -------------------- */
function getIso2(country) {
  return (country?.iso2 || country?.value || "").toString().toUpperCase();
}

const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

const MAX_CONTACT_LEN = 25;

/* -------------------- Levenshtein -------------------- */
function levenshtein(a, b) {
  const m = [];
  for (let i = 0; i <= b.length; i++) m[i] = [i];
  for (let j = 0; j <= a.length; j++) m[0][j] = j;

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      m[i][j] =
        b[i - 1] === a[j - 1]
          ? m[i - 1][j - 1]
          : 1 + Math.min(m[i - 1][j], m[i][j - 1], m[i - 1][j - 1]);
    }
  }
  return m[b.length][a.length];
}

const normalizeEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const splitEmail = (value) => {
  const v = normalizeEmail(value);
  const at = v.lastIndexOf("@");
  if (at <= 0 || at === v.length - 1) return null;

  const local = v.slice(0, at);
  const domain = v.slice(at + 1);

  if (!local || !domain) return null;
  if (!domain.includes(".")) return null;

  return { local, domain };
};

const extractDomainLabelAndSuffix = (domain) => {
  const parts = domain.split(".").filter(Boolean);
  if (parts.length < 2) return null;

  const label = parts[parts.length - 2];
  const suffixChain = parts.slice(parts.length - 1).join(".");
  const suffixFull =
    parts.length >= 3 ? parts.slice(parts.length - 2).join(".") : suffixChain;

  return { label, suffix: suffixFull };
};

const findClosestProvider = (label) => {
  let best = { provider: "", dist: Infinity };

  for (const p of COMMON_EMAIL_PROVIDERS) {
    const d = levenshtein(label, p);
    if (d < best.dist) best = { provider: p, dist: d };
  }

  return best;
};

/* -------------------- Helper: Contact number validation -------------------- */
const validateContactNumber = (value, country) => {
  const v = String(value || "").trim();
  const iso2 = getIso2(country);

  if (!iso2) return "";

  if (!v) return "🚨 Contact number is required.";
  if (v.length < 7)
    return "🚨 Contact number is too short. Must have at least 7 digits.";

  return "";
};

/* -------------------- Order summary model -------------------- */
const pickCouponForItem = (item, coupons) => {
  const list = Array.isArray(coupons) ? coupons : [];

  // item plan id can be in different shapes
  const itemPlanId = String(
    item?._id || item?.plan?._id || item?.price_id || "",
  );
  if (!itemPlanId) return null;

  return (
    list.find((c) => String(c?.plan?._id || c?.plan || "") === itemPlanId) ||
    null
  );
};

const buildPricingForItem = (
  role = "parent",
  item,
  coupons,
  index = null,
  options = {},
) => {
  const baseName =
    typeof item?.name === "string" && item.name.trim() !== ""
      ? item.name.trim()
      : "Untitled product";

  const pricePlanName =
    role === "child" && typeof index === "number"
      ? `Bundle item ${index + 1}: ${baseName}`
      : baseName;

  const currency = item?.currency ? String(item.currency).toUpperCase() : "N/A";

  const amountRaw = Number(item?.price_breakdown?.amount);
  const showAmount = Number.isFinite(amountRaw) && amountRaw > 0;
  const amount = showAmount ? formatMoney(amountRaw, currency) : "N/A";

  const isChild = role === "child";
  const discountRaw = Number(item?.price_breakdown?.discount_amount);
  const hasDiscount =
    !isChild && Number.isFinite(discountRaw) && discountRaw > 0;
  const discountAmount = hasDiscount
    ? formatMoney(discountRaw, currency)
    : "N/A";

  const isRecurring = Boolean(item?.recurring);
  const coupon = pickCouponForItem(item, coupons);

  const selectedGatewayVendor = String(
    options?.selectedGatewayVendor || "",
  ).toLowerCase();

  const isToyyibpay = selectedGatewayVendor === "toyyibpay";

  const trialChargeMessage = isToyyibpay ? "" : buildTrialChargeMessage(coupon);

  const subscriptionLabel = isRecurring
    ? `${item?.interval_count}-${item?.interval} subscription`
    : "Lifetime";

  // ✅ free-trial coupon + CC not required => hide subscription + auto-renewal
  const isFreeTrialCoupon = coupon?.permission === 8;
  const isCcRequired = Boolean(coupon?.free_trial_is_cc_required);
  const freeTrialNoCard = isFreeTrialCoupon && !isCcRequired;

  // Hide the "subscriptionLabel" row for ALL free-trial coupons
  const isBundleContainer =
    Boolean(item?.is_bundled) &&
    Array.isArray(item?.bundles) &&
    item.bundles.length > 0;

  const hideSubscription = isFreeTrialCoupon || isBundleContainer;

  // Hide auto-renewal only when:
  // - free trial doesn't require CC
  // - select Toyyibpay as payment gateway
  const hideAutoRenewal = freeTrialNoCard || isToyyibpay;

  // Next amount (auto-renewal display):
  // Use amount_after_discount for BOTH parent and child.
  const nextAmountRaw = Number(
    item?.next_price_breakdown?.amount_after_discount,
  );
  const showNextAmount = Number.isFinite(nextAmountRaw) && nextAmountRaw > 0;
  const nextAmount = showNextAmount
    ? formatMoney(nextAmountRaw, currency)
    : "N/A";

  const intervalLabel = PIRANHA_INTERVAL_NORMALIZED?.[item?.interval] || "";

  const extensionInterval = coupon?.extension_interval;
  const extensionIntervalCount = coupon?.extension_interval_count;
  const extensionLabel =
    extensionInterval && extensionIntervalCount
      ? `${extensionIntervalCount}-${
          PIRANHA_INTERVAL_RAW?.[extensionInterval] || extensionInterval
        }`
      : null;

  const trialInterval = coupon?.free_trial_interval;
  const trialIntervalCount = coupon?.free_trial_interval_count;
  const trialLabel =
    trialInterval && trialIntervalCount
      ? `${trialIntervalCount}-${
          PIRANHA_INTERVAL_RAW?.[trialInterval] || trialInterval
        }`
      : null;

  let couponBonusText = "";

  if (coupon) {
    if (coupon.permission === 8 && trialLabel) {
      couponBonusText = `${trialLabel} free trial`;
    } else if (extensionLabel) {
      couponBonusText = `Bonus: Free ${extensionLabel} subscription (for 1st year only)`;
    }
  }

  const nextTaxBehavior = String(item?.next_price_breakdown?.tax_behavior || "")
    .trim()
    .toLowerCase();

  const nextTaxRate = Number(item?.next_price_breakdown?.tax_rate);

  const subjectToTax =
    nextTaxBehavior === "exclusive" &&
    Number.isFinite(nextTaxRate) &&
    nextTaxRate > 0;

  return {
    pricePlanName,
    currency,

    showAmount,
    amount,

    hasDiscount,
    discountAmount,

    isRecurring,
    subscriptionLabel,
    hideSubscription,

    coupon,
    couponBonusText,
    trialChargeMessage,

    showNextAmount,
    nextAmount,
    intervalLabel,

    hideAutoRenewal,

    hasCoupon: Boolean(coupon),
    isFreeTrialCoupon,

    subjectToTax,
  };
};

const OrderSummaryItem = ({ model }) => {
  if (!model) return null;

  return (
    <div className='text-start'>
      <h6 className='fw-bold mb-2'>{model.pricePlanName}</h6>

      {model.coupon?.code && (
        <p className='piranha-coupon mb-2'>
          <TicketPerforatedFill /> {model.coupon.code} <sup>**</sup>
        </p>
      )}

      {model.showAmount && (
        <p className='mb-2'>
          <Coin /> Usual price: {model.amount}
          {model.subjectToTax && <sup>*</sup>}
        </p>
      )}

      {model.hasDiscount && (
        <p className='text-danger fw-bold mb-2'>
          <CaretDownFill /> Discount applied: {model.discountAmount}
        </p>
      )}

      {!model.hideSubscription && (
        <p className='mb-2'>
          <Calendar3 /> {model.subscriptionLabel}
        </p>
      )}

      {/* 📝 Start: Disable later */}
      {model.isRecurring && model.couponBonusText && (
        <p className={`fw-bold mb-2`} style={{ color: "#3cad66" }}>
          {model.isFreeTrialCoupon ? (
            <>
              <HandThumbsUpFill className='me-1' />
              {model.couponBonusText}
            </>
          ) : (
            <>
              <CaretUpFill className='me-1' />
              {model.couponBonusText}
            </>
          )}
        </p>
      )}

      {model.isRecurring && model.showNextAmount && !model.hideAutoRenewal && (
        <p className='text-primary fw-bold mb-1'>
          <ArrowClockwise /> Auto-renewal at {model.nextAmount}
          {model.subjectToTax && <sup>*</sup>} {model.intervalLabel} until
          cancelled.
        </p>
      )}
      {/* 📝 End: Disable later */}

      {/* 📝 Start: Enable later */}
      {/* {model.isRecurring && model.couponBonusText && (
        <div>
          <div className='mb-2'>
            <p className='fw-bold mb-0' style={{ color: "#3cad66" }}>
              {model.isFreeTrialCoupon ? (
                <>
                  <HandThumbsUpFill className='me-1' />
                  {model.couponBonusText}
                </>
              ) : (
                <>
                  <CaretUpFill className='me-1' />
                  {model.couponBonusText}
                </>
              )}
            </p>
          </div>

          {model.isFreeTrialCoupon && model.trialChargeMessage && (
            <div className='mb-2'>
              <p
                className='text-primary fw-bold mb-0'
                style={{ lineHeight: 1.5 }}
              >
                <InfoCircleFill className='me-1' />
                {model.trialChargeMessage}
              </p>
            </div>
          )}
        </div>
      )} */}

      {/* ✅ hide auto-renewal row when: */}
      {/* - free-trial */}
      {/* - select Toyyibpay as payment gateway */}
      {/* {model.isRecurring &&
        model.showNextAmount &&
        !model.hideAutoRenewal &&
        !model.isFreeTrialCoupon && (
          <p className='text-primary fw-bold mb-1'>
            <ArrowClockwise /> Auto-renewal at {model.nextAmount}
            {model.subjectToTax && <sup>*</sup>} {model.intervalLabel} until
            cancelled.
          </p>
        )} */}
      {/* 📝 End: Enable later */}

      {model.subjectToTax && (
        <p className='text-muted mb-2' style={{ fontSize: 13 }}>
          * Subject to tax
        </p>
      )}

      {model.hasCoupon && (
        <p className='text-muted fst-italic mb-0' style={{ fontSize: 13 }}>
          ** Terms and conditions apply for this coupon.
        </p>
      )}
    </div>
  );
};

const fetchPlanForCheckout = async (price_slug) => {
  const endpoint_url = `${process.env.REACT_APP_API_ENDPOINT}/api/v1/stores/price-plans/${price_slug}`;
  const url = new URL(endpoint_url);

  const params = new URLSearchParams(window.location.search);
  const coupon = String(params.get("coupon_code") || "").trim();

  if (coupon) url.searchParams.set("coupon_code", coupon);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  let data = null;

  try {
    data = await res.json();
  } catch (error) {
    data = null;
  }

  if (!res.ok) {
    const msg =
      data?.errors?.[0]?.message ||
      data?.error?.message ||
      `Something went wrong. Please try again later.`;

    const err = new Error(msg);
    err.status = res.status;
    err.errors = Array.isArray(data?.errors) ? data.errors : [];
    err.raw = data;

    throw err;
  }

  return data?.payloads ?? null;
};

const recalculatePricing = async ({
  price_slug,
  country_iso2,
  state,
  postal_code,
  coupon_code,
}) => {
  const params = new URLSearchParams();

  if (country_iso2) params.set("country_iso2", country_iso2);
  if (state) params.set("state", state);
  if (postal_code) params.set("postal_code", postal_code);
  if (coupon_code) params.set("coupon_code", coupon_code);

  const url = `${process.env.REACT_APP_API_ENDPOINT}/api/v1/stores/price-plans/${price_slug}/recalculate?${params.toString()}`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Cache-Control": "no-store",
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Recalculate failed (${res.status}): ${text}`);
  }

  const data = await res.json();

  return data?.payloads ?? null;
};

const PriceBreakdownTable = ({
  priceBreakdown,
  currency,
  isRecalculating = false,
}) => {
  if (!priceBreakdown) return null;

  // const taxAmountRaw = Number(priceBreakdown?.tax_amount || 0);
  // const showTaxRow = Number.isFinite(taxRateRaw) && taxRateRaw > 0;

  const taxRateRaw = Number(priceBreakdown?.tax_rate);
  const hasTaxRate = Number.isFinite(taxRateRaw) && taxRateRaw > 0;
  const showTaxRow = Number.isFinite(taxRateRaw) && taxRateRaw > 0;

  const subTotalLabel = showTaxRow ? "Sub total (excl. tax)" : "Sub total";
  const taxLabel = hasTaxRate ? `Tax (${taxRateRaw}%)` : "Tax";
  const currencyLabel = displayCurrency(currency);

  return (
    <div className='position-relative'>
      <table className='table table-sm mb-0'>
        <tbody>
          <tr>
            <td>Currency</td>
            <td className='text-end'>{currencyLabel}</td>
          </tr>

          <tr>
            <td>Amount</td>
            <td className='text-end'>
              {formatMoney(priceBreakdown.amount, currency)}
            </td>
          </tr>

          <tr>
            <td>Discount</td>
            <td className='text-end'>
              {Number(priceBreakdown.discount_amount) > 0 ? "-" : ""}{" "}
              {formatMoney(priceBreakdown.discount_amount, currency)}
            </td>
          </tr>
        </tbody>
      </table>

      <table className='table table-sm mt-4 border border-5'>
        <tbody>
          <tr>
            <td>{subTotalLabel}</td>
            <td className='text-end'>
              {formatMoney(priceBreakdown.sub_total, currency)}
            </td>
          </tr>

          {showTaxRow && (
            <tr>
              <td>{taxLabel}</td>
              <td className='text-end'>
                {formatMoney(priceBreakdown.tax_amount, currency)}
              </td>
            </tr>
          )}

          <tr className='table-secondary border-top'>
            <td className='fw-bold'>Final amount</td>
            <td className='text-end fw-bold'>
              ≈ {formatMoney(priceBreakdown.grand_total, currency)}
            </td>
          </tr>
        </tbody>
      </table>

      <div
        className={`piranha-summary-loading ${isRecalculating ? "is-active" : ""}`}
      >
        <div className='piranha-spinner'></div>
        <div className='piranha-loading-text'>Calculating…</div>
      </div>
    </div>
  );
};

/* -------------------- Parent -------------------- */
const CheckoutForm = () => {
  const { price_slug: price_slug_param } = useParams();

  // ✅ depend on slugs (avoid TDZ + avoid plan-driven loops)
  const price_slug2 = String(price_slug_param || "").trim(); // avoid name clash with useParams

  const [planLoadError, setPlanLoadError] = useState(null);
  const [isRecalculating, setIsRecalculating] = useState(false);

  const [team, setTeam] = useState("");
  const [plan, setPlan] = useState(null);
  const [priceBreakdown, setPriceBreakdown] = useState(null);

  const checkoutPageTitle = useMemo(() => {
    const planName = String(plan?.name || "").trim();
    const teamName = String(team?.name || "").trim();

    if (planName && teamName) return `${planName} | ${teamName}`;
    if (planName) return `${planName}`;
    if (teamName) return `${teamName}`;

    return "Piranha Profits";
  }, [plan?.name, team?.name]);

  // ✅ pricing snapshot for order summary (coupon/location recalcs) without mutating plan
  const [pricingSnapshot, setPricingSnapshot] = useState({
    price_breakdown: null,
    next_price_breakdown: null,
    bundles: null,
    coupons: null,
  });

  // ✅ order summary uses this merged object
  const summaryPlan = useMemo(() => {
    if (!plan) return null;

    return {
      ...plan,
      price_breakdown:
        pricingSnapshot.price_breakdown || plan.price_breakdown || null,
      next_price_breakdown:
        pricingSnapshot.next_price_breakdown ||
        plan.next_price_breakdown ||
        null,
      bundles: Array.isArray(pricingSnapshot.bundles)
        ? pricingSnapshot.bundles
        : plan.bundles,
      coupons: Array.isArray(pricingSnapshot.coupons)
        ? pricingSnapshot.coupons
        : plan.coupons,
    };
  }, [plan, pricingSnapshot]);

  const [couponInput, setCouponInput] = useState("");
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const [couponMessage, setCouponMessage] = useState("");
  const [couponError, setCouponError] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  const normalizeCouponInput = (v) =>
    String(v || "")
      .trim()
      .toUpperCase()
      .replace(/[^\w-]/g, "")
      .slice(0, 64);

  const [fullName, setFullName] = useState("");
  const [fullNameError, setFullNameError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [fullNameTouched, setFullNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const [stateOptions, setStateOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [stateDisabled, setStateDisabled] = useState(true);
  const [stateHint, setStateHint] = useState(
    "Please select country / region first",
  );

  const [postalCode, setPostalCode] = useState("");
  const [postalTouched, setPostalTouched] = useState(false);
  const [postalError, setPostalError] = useState("");
  const [postalDisabled, setPostalDisabled] = useState(true);
  const [postalHint, setPostalHint] = useState(
    "Please select country / region first",
  );

  const [dialingCode, setDialingCode] = useState("");
  const [dialingDisabled, setDialingDisabled] = useState(true);
  const [dialingHint, setDialingHint] = useState(
    "Please select country / region first",
  );

  const [contactNumber, setContactNumber] = useState("");
  const [contactTouched, setContactTouched] = useState(false);
  const [contactError, setContactError] = useState("");

  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementTouched, setAgreementTouched] = useState(false);

  const [gateways, setGateways] = useState([]);
  const [selectedGatewayId, setSelectedGatewayId] = useState("");
  const [loading, setLoading] = useState(true);

  const [geoLocation, setGeoLocation] = useState(null);

  const geoAppliedRef = useRef(false);
  const userTouchedLocationRef = useRef(false);

  // tracking init / de-dup guards for Facebook Pixel / Google analytics
  const pixelInitKeyRef = useRef("");
  const gaInitKeyRef = useRef("");
  const trackedViewKeyRef = useRef("");

  // ✅ support tracking IDs from from payload.tracking
  const getTrackingConfig = (payload) => {
    const fb_pixel = String(payload?.trackings?.fb_pixel || "").trim();

    const fb_pixel_test_code = String(
      payload?.trackings?.fb_pixel_test_code || "",
    ).trim();

    const google_analytics_id = String(
      payload?.trackings?.google_analytics_id || "",
    ).trim();

    return {
      fb_pixel,
      fb_pixel_test_code,
      google_analytics_id,
    };
  };

  // ✅ collect enabled custom checkout page scripts
  const getEnabledCheckoutScripts = (payload) => {
    const scripts = Array.isArray(payload?.checkout_page_scripts)
      ? payload.checkout_page_scripts
      : [];

    return scripts.filter(
      (item) =>
        item?.is_enabled === true && String(item?.snippet || "").trim() !== "",
    );
  };

  const stripeOptions = useMemo(() => ({ locale: "en" }), []);

  const contactDisabled = !getIso2(selectedCountry);

  const validateFullName = (value) => {
    const v = String(value || "").trim();
    if (!v) return "🚨 Full name is required.";
    if (v.length < 3) return "🚨 Full name must be at least 3 characters.";
    return "";
  };

  const validateEmail = (value) => {
    const v = normalizeEmail(value);
    if (!v) return "🚨 Email is required.";

    const parsed = splitEmail(v);
    if (!parsed) return "🚨 Please enter a valid email address.";

    const { local, domain } = parsed;

    if (domain.endsWith(".con"))
      return "🚨 Your email does not look right. Do you mean .com?";

    const meta = extractDomainLabelAndSuffix(domain);
    if (!meta) return "";

    const { label, suffix } = meta;
    if (COMMON_EMAIL_PROVIDERS.includes(label)) return "";

    const { provider, dist } = findClosestProvider(label);
    if (dist <= 2) {
      const suggestionDomain = `${provider}.${suffix}`;
      return `😮 This email domain is not commonly seen. It might be ${local}@${suggestionDomain}?`;
    }

    return "";
  };

  const validatePostal = (value, country) => {
    const iso2 = getIso2(country);
    if (!iso2) return "";
    if (iso2 !== "US" && iso2 !== "CA") return "";

    const raw = String(value || "").trim();
    if (!raw) return "🚨 ZIP / Postal Code is required.";

    const v = raw.replace(/\s+/g, "");
    if (v.length < 3)
      return "🚨 ZIP / Postal Code must be at least 3 characters.";

    return "";
  };

  const lastReqIdRef = useRef(0);

  const applyCoupon = async () => {
    if (!plan) return;

    const c = normalizeCouponInput(couponInput);

    setCouponInput(c);
    setCouponMessage("");
    setCouponError("");

    if (!c) {
      setCouponError("Please enter a coupon code.");
      return;
    }

    const country_iso2 = getIso2(selectedCountry);
    const state = String(selectedState?.name || "").trim() || "";
    const postalTrimmed = String(postalCode || "")
      .trim()
      .toUpperCase();
    const postal_code = postalTrimmed || "";

    setIsApplyingCoupon(true);

    const reqId = ++lastReqIdRef.current;

    try {
      const payloads = await recalculatePricing({
        price_slug: price_slug2,
        country_iso2,
        state,
        postal_code,
        coupon_code: c,
      });

      if (reqId !== lastReqIdRef.current) return;

      const returnedCoupons = Array.isArray(payloads?.coupons)
        ? payloads.coupons
        : [];

      if (!returnedCoupons.length) {
        // 🚨 IMPORTANT: coupon rejected by backend
        setCouponError("Invalid or expired coupon code.");
        setCouponMessage("");
        setAppliedCouponCode("");
        setIsApplyingCoupon(false);
      } else {
        // ✅ valid coupon - update URL to reflect this
        setAppliedCouponCode(c);
        setCouponMessage("Coupon applied.");
      }

      // ✅ valid coupon
      setPricingSnapshot({
        price_breakdown: payloads?.price_breakdown || null,
        next_price_breakdown: payloads?.next_price_breakdown || null,
        bundles: Array.isArray(payloads?.bundles) ? payloads.bundles : null,
        coupons: returnedCoupons,
      });

      setPriceBreakdown(payloads?.price_breakdown || null);
    } catch (err) {
      if (reqId === lastReqIdRef.current) {
        setCouponError(err?.message || "Failed to apply coupon.");
      }
    } finally {
      if (reqId === lastReqIdRef.current) setIsApplyingCoupon(false);
    }
  };

  const selectedGatewayVendor = useMemo(() => {
    const gateway = Array.isArray(gateways)
      ? gateways.find(
          (item) => String(item?._id || "") === String(selectedGatewayId || ""),
        )
      : null;

    return String(gateway?.vendor || "").toLowerCase();
  }, [gateways, selectedGatewayId]);

  useEffect(() => {
    document.title = checkoutPageTitle;
  }, [checkoutPageTitle]);

  useEffect(() => {
    if (!getIso2(selectedCountry)) setContactNumber("");
  }, [selectedCountry]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const urlCouponRaw = getCouponCodeFromUrl();
      const urlCoupon = normalizeCouponInput(urlCouponRaw);

      // reset coupon UI each load
      setCouponError("");
      setCouponMessage("");

      setCouponInput(urlCoupon);
      // IMPORTANT: don't auto-trust it as "applied" yet
      setAppliedCouponCode("");

      let data = null;

      try {
        data = await fetchPlanForCheckout(price_slug2);
        setPlanLoadError(null);

        // coupon
        const returnedCoupons = Array.isArray(data?.coupons)
          ? data.coupons
          : [];

        const hasUrlCoupon = Boolean(urlCoupon);
        if (hasUrlCoupon && !returnedCoupons.length) {
          // 🚨 invalid coupon from URL
          setAppliedCouponCode("");
          setCouponMessage("");
          setCouponError("Invalid or expired coupon code.");
        } else if (hasUrlCoupon && returnedCoupons.length) {
          // ✅ valid coupon confirmed by backend
          setAppliedCouponCode(urlCoupon);
          setCouponMessage("Coupon applied.");
        }
      } catch (err) {
        const status = err?.status || 0;
        const errors = Array.isArray(err?.errors) ? err.errors : [];
        const message = err?.message || "Failed to load plan.";
        setPlanLoadError({ status, errors, message });
        setLoading(false);
        return;
      }

      const geo = data?.geolocation || null;

      setTeam(data?.team || "");
      setPlan(data);
      setPriceBreakdown(data?.price_breakdown || null);

      // ✅ seed pricing snapshot from initial plan load
      setPricingSnapshot({
        price_breakdown: data?.price_breakdown || null,
        next_price_breakdown: data?.next_price_breakdown || null,
        bundles: Array.isArray(data?.bundles) ? data.bundles : null,
        coupons: Array.isArray(data?.coupons) ? data.coupons : null,
      });

      geoAppliedRef.current = false;

      setFullName("");
      setEmail("");
      setFullNameError("");
      setEmailError("");
      setFullNameTouched(false);
      setEmailTouched(false);

      setPostalCode("");
      setPostalTouched(false);
      setPostalError("");
      setPostalDisabled(true);
      setPostalHint("Please select country / region first");

      setDialingCode("");
      setDialingDisabled(true);
      setDialingHint("Please select country / region first");

      setContactNumber("");
      setContactTouched(false);
      setContactError("");

      setAgreementAccepted(false);
      setAgreementTouched(false);

      setStateOptions([]);
      setSelectedState(null);
      setStateDisabled(true);
      setStateHint("Please select country / region first");

      setGeoLocation(geo);

      try {
        const results = await Promise.resolve(GetCountries());

        const countryLists = (results || []).map((result, index) => ({
          index,
          value: result.iso2 ?? result.id,
          label: `${result.emoji || ""} ${result.name} (${result.iso2})`,
          name: result.name,
          iso2: result.iso2,
          iso3: result.iso3,
          phone_code: result.phone_code ? `+${result.phone_code}` : "",
          _raw: result,
        }));

        if (!mounted) return;

        setCountryOptions(countryLists);

        const geoIso2 = String(geo?.country_iso2 || "").toUpperCase();
        if (geoIso2) {
          const match = countryLists.find(
            (c) => String(c.iso2 || "").toUpperCase() === geoIso2,
          );
          setSelectedCountry(match || null);
        } else {
          setSelectedCountry(null);
        }
      } catch (err) {
        if (mounted) setCountryOptions([]);
        if (mounted) setSelectedCountry(null);
      }

      const list = (data?.payment_gateway_options || []).filter(
        (g) => g?.status,
      );
      setGateways(list);

      if (!list.length) setSelectedGatewayId("");
      else setSelectedGatewayId(list[0]?._id || "");

      setLoading(false);
    };

    load();
    return () => {
      mounted = false;
    };
  }, [price_slug2]);

  useEffect(() => {
    let mounted = true;

    const loadStatesAndCountryDependentFields = async () => {
      setSelectedState(null);
      setStateOptions([]);

      setPostalCode("");
      setPostalTouched(false);
      setPostalError("");

      setContactNumber("");
      setContactTouched(false);
      setContactError("");

      const iso2 = getIso2(selectedCountry);

      const isGeoMatch =
        !geoAppliedRef.current &&
        geoLocation &&
        String(geoLocation?.country_iso2 || "").toUpperCase() === iso2;

      if (!iso2) {
        setDialingCode("");
        setDialingDisabled(true);
        setDialingHint("Please select country / region first");
      } else {
        const code = String(selectedCountry?.phone_code || "").trim();
        setDialingCode(code);
        setDialingDisabled(true);
        setDialingHint(code ? "" : "Not available");
      }

      if (!iso2) {
        setPostalDisabled(true);
        setPostalHint("Please select country / region first");
      } else if (iso2 === "US") {
        setPostalDisabled(false);
        setPostalHint("Required for United States");
      } else if (iso2 === "CA") {
        setPostalDisabled(false);
        setPostalHint("Required for Canada");
      } else {
        setPostalDisabled(true);
        setPostalHint("Not required");
      }

      if (
        isGeoMatch &&
        (iso2 === "US" || iso2 === "CA") &&
        String(geoLocation?.postal_code || "").trim()
      ) {
        setPostalCode(String(geoLocation.postal_code).trim().toUpperCase());
      }

      if (!iso2) {
        setStateDisabled(true);
        setStateHint("Please select country / region first");
        return;
      }

      if (iso2 === "SG") {
        setStateDisabled(true);
        setStateHint("Not required");
        if (isGeoMatch) geoAppliedRef.current = true;
        return;
      }

      setStateDisabled(true);
      setStateHint("Loading states...");

      try {
        const countryId =
          selectedCountry?._raw?.id ||
          selectedCountry?._raw?.iso2 ||
          selectedCountry?.value;

        const results = await Promise.resolve(GetState(countryId));

        const states = (results || []).map((s, index) => ({
          index,
          value: s.id ?? s.iso2 ?? `${s.name}-${index}`,
          label: s.name,
          name: s.name,
          iso2: s.iso2,
          country_code: s.country_code,
          _raw: s,
        }));

        if (!mounted) return;

        setStateOptions(states);

        if (!states.length) {
          setStateDisabled(true);
          setStateHint("Not required as no states available");
          if (isGeoMatch) geoAppliedRef.current = true;
          return;
        }

        setStateDisabled(false);
        setStateHint("Select state / territory");

        if (isGeoMatch && String(geoLocation?.state_name || "").trim()) {
          const target = String(geoLocation.state_name || "")
            .trim()
            .toLowerCase();
          const match = states.find(
            (s) =>
              String(s.name || "")
                .trim()
                .toLowerCase() === target,
          );
          if (match) setSelectedState(match);
          geoAppliedRef.current = true;
        }
      } catch (err) {
        if (!mounted) return;

        setStateOptions([]);
        setStateDisabled(true);
        setStateHint("Not required as no states available");

        if (isGeoMatch) geoAppliedRef.current = true;
      }

      if (isGeoMatch && !String(geoLocation?.state_name || "").trim()) {
        geoAppliedRef.current = true;
      }
    };

    loadStatesAndCountryDependentFields();

    return () => {
      mounted = false;
    };
  }, [selectedCountry, geoLocation]);

  useEffect(() => {
    if (!userTouchedLocationRef.current) return;

    // ✅ depend on slugs (not plan), so setPricingSnapshot won't retrigger this effect
    if (!price_slug2) return;

    const country_iso2 = getIso2(selectedCountry);
    const needsStatePostal = country_iso2 === "US" || country_iso2 === "CA";

    const state = String(selectedState?.name || "").trim() || null;
    const postalTrimmed = String(postalCode || "")
      .trim()
      .toUpperCase();
    const postal_code = postalTrimmed || null;

    const hasCountry = Boolean(country_iso2);
    const hasState = Boolean(state);
    const hasPostalMin3 = postalTrimmed.length >= 3;

    const canRecalculate = needsStatePostal
      ? hasCountry && hasState && hasPostalMin3
      : hasCountry;

    if (!canRecalculate) {
      setIsRecalculating(false);
      return;
    }

    const debounceMs = needsStatePostal ? 800 : 0;
    const reqId = ++lastReqIdRef.current;

    const debounceTimer = setTimeout(async () => {
      setIsRecalculating(true);

      try {
        const payloads = await recalculatePricing({
          price_slug: price_slug2,
          country_iso2,
          state,
          postal_code,
          coupon_code: appliedCouponCode,
        });

        if (reqId !== lastReqIdRef.current) return;

        setPriceBreakdown(payloads?.price_breakdown || null);

        // ✅ update pricing snapshot for ORDER SUMMARY
        setPricingSnapshot({
          price_breakdown: payloads?.price_breakdown || null,
          next_price_breakdown: payloads?.next_price_breakdown || null,
          bundles: Array.isArray(payloads?.bundles) ? payloads.bundles : null,
          coupons: Array.isArray(payloads?.coupons) ? payloads.coupons : null,
        });
      } catch (err) {
        if (reqId === lastReqIdRef.current) console.error(err);
      } finally {
        if (reqId === lastReqIdRef.current) setIsRecalculating(false);
      }
    }, debounceMs);

    return () => clearTimeout(debounceTimer);
  }, [
    selectedCountry,
    selectedState,
    postalCode,
    price_slug2,
    appliedCouponCode,
  ]);

  // ✅ inject custom checkout page scripts when provided
  useEffect(() => {
    if (!plan) return;

    const enabledScripts = getEnabledCheckoutScripts(plan);

    if (!enabledScripts.length) return;

    const cleanup = injectScripts(enabledScripts);

    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [plan]);

  // fire Meta Pixel + Google Analytics after checkout payload is loaded
  useEffect(() => {
    if (!plan) return;

    const enabledScripts = getEnabledCheckoutScripts(plan);

    // ✅ custom tracking takes priority
    if (enabledScripts.length > 0) return;

    const tracking = getTrackingConfig(plan);

    const fbPixelId = tracking.fb_pixel;
    const fbPixelTestCode = tracking.fb_pixel_test_code;
    const gaId = tracking.google_analytics_id;

    const pagePath = `${window.location.pathname}${window.location.search}`;
    const viewKey = [price_slug2, plan?._id || "", fbPixelId, gaId].join("|");

    // prevent duplicate ViewContent / pageview fires for same loaded checkout
    if (trackedViewKeyRef.current === viewKey) return;
    trackedViewKeyRef.current = viewKey;

    // ----------------------------
    // Meta Pixel
    // ----------------------------
    if (fbPixelId) {
      const pixelInitKey = `${fbPixelId}::${fbPixelTestCode}`;

      if (pixelInitKeyRef.current !== pixelInitKey) {
        const pixelOptions = fbPixelTestCode
          ? { testEventCode: fbPixelTestCode }
          : undefined;

        ReactPixel.init(fbPixelId, {}, pixelOptions);
        pixelInitKeyRef.current = pixelInitKey;
      }

      ReactPixel.track("InitiateCheckout");

      // ReactPixel.pageView();

      // ReactPixel.track("InitiateCheckout", {
      //   content_name: plan?.name || "",
      //   content_ids: [String(plan?._id || plan?.price_id || price_slug2 || "")],
      //   content_type: "product",
      //   currency: normalizeCurrency(plan?.currency),
      //   value: Number(plan?.price_breakdown?.grand_total || 0) / 100,
      // });
    }

    // ----------------------------
    // Google Analytics 4
    // ----------------------------
    if (gaId) {
      if (gaInitKeyRef.current !== gaId) {
        ReactGA.initialize(gaId);
        gaInitKeyRef.current = gaId;
      }

      ReactGA.event({
        category: "Ecommerce",
        action: "Begin Checkout",
      });

      // ReactGA.send({
      //   hitType: "pageview",
      //   page: pagePath,
      //   title: document.title,
      // });

      // ReactGA.event("view_item", {
      //   currency: normalizeCurrency(plan?.currency),
      //   value: Number(plan?.price_breakdown?.grand_total || 0) / 100,
      //   items: [
      //     {
      //       item_id: String(plan?._id || plan?.price_id || price_slug2 || ""),
      //       item_name: plan?.name || "",
      //     },
      //   ],
      // });
    }
  }, [plan, price_slug2]);

  if (loading)
    return (
      <div className='vh-100 d-flex flex-column align-items-center justify-content-center'>
        {/* Spinner Row */}
        <div className='d-flex gap-3 mb-3'>
          <div className='spinner-grow text-danger' role='status' />
          <div className='spinner-grow text-warning' role='status' />
          <div className='spinner-grow text-success' role='status' />

          <div className='spinner-grow text-primary' role='status' />
          <div className='spinner-grow text-info' role='status' />

          <div className='spinner-grow text-secondary' role='status' />
          <div className='spinner-grow text-dark' role='status' />
        </div>

        {/* Text */}
        <p className='mb-0'>Loading checkout form...</p>
      </div>
    );

  if (planLoadError && !plan) {
    return (
      <div className='container py-4'>
        <div className='piranha-error-box'>
          <div className='piranha-error-title'>
            Unable to load the checkout form
          </div>
        </div>

        <div
          className='my-3 border rounded-4 p-3'
          dangerouslySetInnerHTML={{ __html: String(planLoadError.message) }}
        />
      </div>
    );
  }

  const currency = normalizeCurrency(plan?.currency);

  // free-trial coupon & cc is not required no card UI required
  const parentCoupon = pickCouponForItem(summaryPlan, summaryPlan?.coupons);

  const freeTrialNoCard =
    parentCoupon?.permission === 8 && !parentCoupon?.free_trial_is_cc_required;

  return (
    <div className='container'>
      <div className='row my-3'>
        <Link to={`/`} className='text-center'>
          <img
            className='img-fluid checkout-logo'
            src={`/pp_fish_image.webp`}
            alt='piranha profits logo'
          />
        </Link>
      </div>

      <div className='row gx-3 gy-3 gy-lg-0'>
        <div className='col-sm-12 col-md-12 col-lg-6 order-2 order-lg-1'>
          <div className='shadow border bg-white p-3 rounded-4'>
            <form>
              <fieldset className='border rounded p-3 my-3 piranha-fieldset-green'>
                <legend className='float-none w-auto px-2'>
                  Billing details
                </legend>

                <div className='mb-3 fw-bold text-start'>
                  <label className='text-uppercase'>Full Name</label>

                  {fullNameTouched && fullNameError && (
                    <div className='text-danger fw-bold mt-1'>
                      {fullNameError}
                    </div>
                  )}

                  <div className='p-1 border rounded-3 bg-white'>
                    <input
                      type='text'
                      className='form-control border-0 shadow-none'
                      value={fullName}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFullName(value);

                        if (fullNameTouched)
                          setFullNameError(validateFullName(value));
                        else setFullNameError("");
                      }}
                      onBlur={(e) => {
                        setFullNameTouched(true);
                        setFullNameError(validateFullName(e.target.value));
                      }}
                    />
                  </div>
                </div>

                <div className='fw-bold text-start'>
                  <label className='text-uppercase'>Email Address</label>
                  {emailTouched && emailError && (
                    <div className='text-danger fw-bold mt-1'>{emailError}</div>
                  )}
                  <div className='p-1 border rounded-3 bg-white'>
                    <input
                      type='email'
                      className='form-control border-0 shadow-none'
                      value={email}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEmail(value);

                        if (emailTouched) setEmailError(validateEmail(value));
                        else setEmailError("");
                      }}
                      onBlur={(e) => {
                        setEmailTouched(true);
                        setEmailError(validateEmail(e.target.value));
                      }}
                    />
                  </div>

                  <div
                    className='text-muted mt-1'
                    style={{ fontSize: 12, fontWeight: "400" }}
                  >
                    Note: If you have an existing Piranha Profits’s account,
                    please enter the same email address.
                  </div>
                </div>

                <div className='fw-bold text-start mt-3'>
                  <label className='text-uppercase'>Country / Region</label>
                  <div className='p-1 border rounded-3 bg-white d-flex align-items-center'>
                    <div className='flex-grow-1'>
                      <Select
                        value={selectedCountry}
                        onChange={(opt, meta) => {
                          if (
                            meta?.action === "select-option" ||
                            meta?.action === "clear"
                          ) {
                            userTouchedLocationRef.current = true;
                          }
                          setSelectedCountry(opt);
                        }}
                        options={countryOptions}
                        placeholder='Select country / region'
                        classNamePrefix='piranha-select'
                        styles={{
                          control: (base) => ({
                            ...base,
                            border: "none",
                            boxShadow: "none",
                            minHeight: "unset",
                            fontWeight: 400,
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            padding: 0,
                            fontWeight: 400,
                            paddingLeft: 12,
                            paddingRight: 12,
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontWeight: 400,
                          }),
                          placeholder: (base) => ({
                            ...base,
                            fontWeight: 400,
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                            fontWeight: 400,
                          }),
                          option: (base, item) => ({
                            ...base,
                            fontWeight: 400,
                            backgroundColor: item.isFocused
                              ? "#f5f5f5"
                              : item.isSelected
                                ? "#eaeaea"
                                : "white",
                            color: "#212529",
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            paddingTop: 0,
                            paddingBottom: 0,
                          }),
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className='fw-bold text-start mt-3'>
                  <label className='text-uppercase'>State / Territory</label>
                  <div className='p-1 border rounded-3 bg-white d-flex align-items-center'>
                    <div className='flex-grow-1'>
                      <Select
                        value={selectedState}
                        onChange={(opt, meta) => {
                          if (
                            meta?.action === "select-option" ||
                            meta?.action === "clear"
                          ) {
                            userTouchedLocationRef.current = true;
                          }
                          setSelectedState(opt);
                        }}
                        options={stateOptions}
                        placeholder={stateHint}
                        isDisabled={stateDisabled}
                        classNamePrefix='piranha-select'
                        styles={{
                          control: (base) => ({
                            ...base,
                            border: "none",
                            boxShadow: "none",
                            minHeight: "unset",
                            fontWeight: 400,
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            padding: 0,
                            fontWeight: 400,
                            paddingLeft: 12,
                            paddingRight: 12,
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontWeight: 400,
                          }),
                          placeholder: (base) => ({
                            ...base,
                            fontWeight: 400,
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                            fontWeight: 400,
                          }),
                          option: (base, item) => ({
                            ...base,
                            fontWeight: 400,
                            backgroundColor: item.isFocused
                              ? "#f5f5f5"
                              : item.isSelected
                                ? "#eaeaea"
                                : "white",
                            color: "#212529",
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            paddingTop: 0,
                            paddingBottom: 0,
                          }),
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className='fw-bold text-start mt-3'>
                  <label className='text-uppercase'>ZIP / Postal Code</label>

                  {postalTouched && postalError && (
                    <div className='text-danger fw-bold mt-1'>
                      {postalError}
                    </div>
                  )}

                  <div className='p-1 border rounded-3 bg-white'>
                    <input
                      type='text'
                      className='form-control border-0 shadow-none'
                      value={postalCode}
                      disabled={postalDisabled}
                      placeholder={postalHint}
                      onChange={(e) => {
                        userTouchedLocationRef.current = true;
                        const value = String(
                          e.target.value || "",
                        ).toUpperCase();

                        setPostalCode(value);

                        if (postalTouched) {
                          setPostalError(
                            validatePostal(value, selectedCountry),
                          );
                        } else {
                          setPostalError("");
                        }
                      }}
                      onBlur={(e) => {
                        setPostalTouched(true);
                        setPostalError(
                          validatePostal(e.target.value, selectedCountry),
                        );
                      }}
                    />
                  </div>
                </div>

                <div className='fw-bold text-start mt-3'>
                  <label className='text-uppercase'>Dialing Code</label>
                  <div className='p-1 border rounded-3 bg-white'>
                    <input
                      type='text'
                      className='form-control border-0 shadow-none'
                      value={dialingCode}
                      disabled={dialingDisabled}
                      placeholder={dialingHint}
                      readOnly
                    />
                  </div>
                </div>

                <div className='fw-bold text-start mt-3'>
                  <label className='text-uppercase'>Contact Number</label>

                  {contactTouched && contactError && (
                    <div className='text-danger fw-bold mt-1'>
                      {contactError}
                    </div>
                  )}

                  <div className='p-1 border rounded-3 bg-white'>
                    <input
                      type='tel'
                      inputMode='numeric'
                      pattern='[0-9]*'
                      className='form-control border-0 shadow-none'
                      value={contactNumber}
                      disabled={contactDisabled}
                      placeholder={
                        contactDisabled
                          ? "Please select country / region first"
                          : ""
                      }
                      onChange={(e) => {
                        const clean = digitsOnly(e.target.value).slice(
                          0,
                          MAX_CONTACT_LEN,
                        );
                        setContactNumber(clean);

                        if (contactTouched) {
                          setContactError(
                            validateContactNumber(clean, selectedCountry),
                          );
                        } else {
                          setContactError("");
                        }
                      }}
                      onBlur={() => {
                        setContactTouched(true);
                        setContactError(
                          validateContactNumber(contactNumber, selectedCountry),
                        );
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasted = digitsOnly(
                          e.clipboardData.getData("text"),
                        );
                        setContactNumber((prev) =>
                          `${prev}${pasted}`.slice(0, MAX_CONTACT_LEN),
                        );
                      }}
                    />
                  </div>

                  <div
                    className='text-muted mt-1'
                    style={{ fontSize: 12, fontWeight: "400" }}
                  >
                    Note: Please fill in your contact number without dialing
                    code.
                  </div>
                </div>
              </fieldset>

              <fieldset className='border rounded p-3 my-3 piranha-fieldset-green'>
                <legend className='float-none w-auto px-2'>Agreement</legend>

                <div className='text-start'>
                  <div className='mb-2'>By ticking the box below, you:</div>

                  <div
                    dangerouslySetInnerHTML={{
                      __html: plan?.legal_contents_html,
                    }}
                  />

                  {selectedGatewayVendor &&
                    selectedGatewayVendor !== "toyyibpay" && (
                      <div
                        className='text-muted fst-italic mb-3'
                        style={{ fontSize: 14 }}
                      >
                        Note: If you bought a subscription,{" "}
                        {team?.company_name || "we"} will automatically renew it
                        and charge your payment method until you cancel.
                      </div>
                    )}

                  <div className='form-check'>
                    <input
                      className='form-check-input piranha-checkbox-lg'
                      type='checkbox'
                      id='piranha_agreement_checkbox'
                      checked={agreementAccepted}
                      onChange={(e) => setAgreementAccepted(e.target.checked)}
                      onBlur={() => setAgreementTouched(true)}
                    />
                    <label
                      className='form-check-label fw-bold'
                      htmlFor='piranha_agreement_checkbox'
                    >
                      Yes, I’ve read and agree to all of the above.
                    </label>
                  </div>

                  {agreementTouched && !agreementAccepted && (
                    <div className='text-danger fw-bold my-2'>
                      🚨 You must agree to the terms before completing your
                      purchase.
                    </div>
                  )}
                </div>
              </fieldset>

              <fieldset className='border rounded p-3 my-3 piranha-fieldset'>
                <legend className='float-none w-auto px-2'>
                  Plan overview
                </legend>

                <p className='fw-bold p-2 mb-3' style={{ fontSize: "1.3rem" }}>
                  <Check2Square /> {plan?.name || "—"}
                </p>

                {priceBreakdown && (
                  <PriceBreakdownTable
                    priceBreakdown={priceBreakdown}
                    currency={currency}
                    isRecalculating={isRecalculating}
                  />
                )}
              </fieldset>
            </form>

            {/* ✅ Payment Method section moved to gateways/PaymentSection */}
            <fieldset className='border rounded p-3 my-3 piranha-fieldset-green'>
              <legend className='float-none w-auto px-2'>Payment method</legend>

              <PaymentSection
                gateways={gateways}
                selectedGatewayId={selectedGatewayId}
                setSelectedGatewayId={setSelectedGatewayId}
                price_slug={price_slug2}
                fullName={fullName}
                email={email}
                selectedCountry={selectedCountry}
                selectedState={selectedState}
                stateOptions={stateOptions}
                postalCode={postalCode}
                dialingCode={dialingCode}
                contactNumber={contactNumber}
                agreementAccepted={agreementAccepted}
                couponCode={appliedCouponCode}
                stripeOptions={stripeOptions}
                freeTrialNoCard={freeTrialNoCard}
              />
            </fieldset>
          </div>
        </div>

        <div className='col-sm-12 col-md-12 col-lg-6 order-1 order-lg-2'>
          <div className='border bg-white p-3 rounded-4'>
            <fieldset className='border rounded p-3 my-3 piranha-fieldset'>
              <legend className='float-none w-auto px-2'>Order summary</legend>

              <div className='text-muted'>
                {!summaryPlan ? (
                  "Loading..."
                ) : (
                  <>
                    <OrderSummaryItem
                      model={buildPricingForItem(
                        "parent",
                        summaryPlan,
                        summaryPlan?.coupons,
                        null,
                        { selectedGatewayVendor },
                      )}
                    />

                    {Array.isArray(summaryPlan?.bundles) &&
                      summaryPlan.bundles
                        .filter(Boolean)
                        .map((bundle, index) => (
                          <div
                            key={bundle._id || bundle.price_id || index}
                            style={{
                              border: "1px solid lightgrey",
                              padding: "20px",
                              margin: "10px",
                              borderRadius: "20px",
                              backgroundColor: "rgb(248, 249, 250)",
                            }}
                          >
                            <OrderSummaryItem
                              model={buildPricingForItem(
                                "child",
                                bundle,
                                summaryPlan?.coupons,
                                index,
                                { selectedGatewayVendor },
                              )}
                            />
                          </div>
                        ))}
                  </>
                )}
              </div>
            </fieldset>

            <fieldset className='border rounded p-3 my-3 piranha-fieldset'>
              <legend className='float-none w-auto px-2'>
                Pricing summary
              </legend>
              <div>
                {priceBreakdown && (
                  <PriceBreakdownTable
                    priceBreakdown={priceBreakdown}
                    currency={currency}
                    isRecalculating={isRecalculating}
                  />
                )}
              </div>
            </fieldset>

            <fieldset className='border rounded p-3 my-3 piranha-fieldset'>
              <legend className='float-none w-auto px-2'>
                Have a coupon code?
              </legend>

              <div className='d-flex gap-2'>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Enter coupon code'
                  value={couponInput}
                  onChange={(e) => {
                    const v = normalizeCouponInput(e.target.value);
                    setCouponInput(v);
                    setCouponError("");
                    setCouponMessage("");
                  }}
                  style={{ flex: 1 }}
                />

                <button
                  type='button'
                  className='btn btn-dark fw-bold px-4'
                  onClick={applyCoupon}
                  disabled={
                    !Boolean(normalizeCouponInput(couponInput)) ||
                    isApplyingCoupon
                  }
                >
                  Apply
                </button>
              </div>

              {couponError && (
                <div className='text-danger fw-bold mt-2'>{couponError}</div>
              )}
              {couponMessage && (
                <div className='text-success fw-bold mt-2'>{couponMessage}</div>
              )}
            </fieldset>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutForm;
